import {Component, EventEmitter, inject, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {NewGameService} from "../../../services/new-game.service";
import {IInitNewGame} from "../../../interfaces/new-game/breakdown/IInitNewGame";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {MatCard} from "@angular/material/card";
import {MatError, MatFormField, MatHint, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatButton} from "@angular/material/button";
import {ActiveUserService} from "../../../services/active-user.service";
import {MatOption, MatSelect} from "@angular/material/select";
import {GeographyService} from "../../../services/geography.service";
import {ICountry} from "../../../interfaces/ICountry";
import {IRegion} from "../../../interfaces/IRegion";

@Component({
  selector: 'app-init-game',
  standalone: true,
  templateUrl: './init-game.component.html',
  imports: [
    MatCard,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatHint,
    MatInput,
    MatError,
    MatProgressSpinner,
    MatButton,
    MatSelect,
    MatOption
  ],
  styleUrl: './init-game.component.css'
})
export class InitGameComponent implements OnInit {

  @Output() newGameWasInitiated = new EventEmitter();

  private newGameService: NewGameService = inject(NewGameService);
  private activeUserService: ActiveUserService = inject(ActiveUserService);
  private geographyService: GeographyService = inject(GeographyService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private gameNameRegex: RegExp = /^[a-zA-Z0-9\- ]+$/;
  private subdomainRegex: RegExp = /^[a-zA-Z][a-zA-Z0-9\-\.]*[a-zA-Z0-9]$|^[a-zA-Z]$/;

  public countries: ICountry[] = [];
  public regions: IRegion[] = [];
  public countriesLoaded = false;

  uiState: UIStateEnum = UIStateEnum.ShowData;
  uiStateEnumForTemplate = UIStateEnum;

  initGameForm = new FormGroup({
    name: new FormControl<string>('', [Validators.required, Validators.pattern(this.gameNameRegex)]),
    subdomain: new FormControl<string>('', [
      Validators.required,
      Validators.maxLength(63),
      Validators.pattern(this.subdomainRegex)
    ]),
    country: new FormControl<string>('', [Validators.required]),
    region: new FormControl<string>('', [Validators.required])
  });

  ngOnInit(): void {
    this.populateCountries();
  }

  onCountryChange() {
    const countryId = this.initGameForm.controls.country.value;
    if (countryId) {
      const selectedCountry = this.countries.find(c => c.Id === countryId);
      this.regions = selectedCountry?.Regions || [];
      this.initGameForm.controls.region.reset();
    }
  }

  startGameCreation() {
    if (!this.initGameForm.valid) {
      this.initGameForm.markAllAsTouched();
      return;
    }

    this.uiState = UIStateEnum.ShowLoading;

    let initGame: IInitNewGame = {
      name: this.initGameForm.controls.name.value!,
      subdomain: this.initGameForm.controls.subdomain.value!,
      countryId: this.initGameForm.controls.country.value!,
      regionId: this.initGameForm.controls.region.value!
    };

    this.newGameService.initNewGame(initGame, this.activeUserService.activeUser().Id).subscribe({
      next: () => {
        this.uiState = UIStateEnum.ShowData;
        this.newGameWasInitiated.emit(true);
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  populateCountries() {
    this.geographyService.allCountriesWithRegions().subscribe({
      next: (countriesP) => {
        this.countries = countriesP;
        this.countriesLoaded = true;
      },
      error: (error) => {
        this.countriesLoaded = true;
        this.errorHandlingService.displayDialogLevelErrorMessage(error);
      }
    });
  }
}
